import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { Constant } from "../../../helper/constant";

import "./modalProductInfo.css";
import { formatDate } from "../../../helper/formatDate";
import PaginationFooter from "../../../components/footer/paginationFooter";

const ModalProductInfo = ({ isShowModal, onCloseModal, productData }) => {
  // =====================
  // Constant Definition
  // =====================

  const navigate = useNavigate();
  const [isShowLoading, setIsShowLoading] = useState(false);
  const [productNoteData, setProductNoteData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [totalStockChanges, setTotalStockChange] = useState(0);
  const [noteStockChanges, setNoteStockChanges] = useState("");
  const [stockExchangeData, setStockExchangeData] = useState("plus");
  const userData = JSON.parse(localStorage.getItem("user_data"));

  // =====================
  // Function Definition
  // =====================

  const closeModal = () => {
    setTotalStockChange(0);
    setNoteStockChanges("");
    onCloseModal();
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  const handleInputField = (field, value) => {
    switch (field) {
      case "total_stock":
        setTotalStockChange(Number(value));
        break;
      case "note":
        setNoteStockChanges(value);
        break;
      default:
        break;
    }
  };

  // =====================
  // Network Definition
  // =====================

  const getProductNote = useCallback(async () => {
    try {
      setIsShowLoading(true);
      const url = `${Constant.BASE_API_URL}/note-produk/produk?product_id=${productData.product_id}&page=${page}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;
      setProductNoteData(data.product_notes || []);
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      console.error(error);
      setProductNoteData([]);
    } finally {
      setIsShowLoading(false);
    }
  }, [navigate, productData.product_id, page]);

  const createNewNote = async () => {
    try {
      setIsShowLoading(true);
      const staffData = localStorage.getItem("user_data");
      const parsedStaffData = JSON.parse(staffData);
      const url = `${Constant.BASE_API_URL}/note-produk/crete-and-update-stock`;
      var currentStock = Number(productData.product_stock);
      var modifiedStock = 0;
      if (stockExchangeData === "plus") {
        modifiedStock = currentStock + Number(totalStockChanges);
      } else {
        modifiedStock = currentStock - Number(totalStockChanges);
      }
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          product_id: productData.product_id,
          product_name: productData.product_name,
          product_transaction_note_message:
            noteStockChanges +
            ` (perubahan stock dari ${productData.product_stock} ke ${modifiedStock})`,
          product_transaction_note_date: new Date(),
          staff_id: parsedStaffData.staff_id,
          staff_name: parsedStaffData.staff_name,
          product_current_stock: productData.product_stock,
          product_transaction_quantity_changes: modifiedStock,
          product_stock_history: modifiedStock,
        }),
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;
      setProductNoteData(data.product_notes || []);
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      console.error(error);
      setProductNoteData([]);
    } finally {
      closeModal();
    }
  };

  // =====================
  // UseEffect Definition
  // =====================

  useEffect(() => {
    if (isShowModal) {
      getProductNote();
    }
  }, [page, isShowModal, getProductNote]);

  // =====================
  // Layout Definition
  // =====================

  return createPortal(
    isShowModal && (
      <div className="modal-product-note-container">
        <div className="modal-product-note-content">
          <header className="modal-product-note-header">
            <h2 className="modal-product-note-header-title">
              {"Kartu Stok " + productData.product_name}
            </h2>
            <button
              className="modal-header-product-note-close-button"
              onClick={closeModal}
            >
              &times;
            </button>
          </header>
          <div className="modal-product-note-main-content">
            <p>
              Stok Sekarang: <b>{productData.product_stock}</b>
            </p>
            {userData != null &&
              (userData.staff_system_role === "ADMIN" ||
                userData.staff_system_role === "MANAGER") && (
                <div className="product-note-update-stock">
                  <p>ubah stok: </p>
                  <label>
                    <input
                      type="radio"
                      name="stock-exchange-status"
                      value="plus"
                      checked={stockExchangeData === "plus"}
                      onChange={(e) => setStockExchangeData("plus")}
                    />
                    +
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="stock-exchange-status"
                      value="minus"
                      checked={stockExchangeData === "minus"}
                      onChange={(e) => setStockExchangeData("minus")}
                    />
                    -
                  </label>
                  <input
                    type="text"
                    className="stock-input"
                    placeholder="total stok"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                    value={totalStockChanges}
                    onChange={(e) =>
                      handleInputField("total_stock", e.target.value)
                    }
                  />
                </div>
              )}
            {userData != null &&
              (userData.staff_system_role === "ADMIN" ||
                userData.staff_system_role === "MANAGER") && (
                <div>
                  <input
                    type="text"
                    placeholder="catatan"
                    className="product-info-note-stock-changes"
                    value={noteStockChanges}
                    onChange={(e) => handleInputField("note", e.target.value)}
                  />
                  <button
                    type="submit"
                    className="submit-button"
                    onClick={createNewNote}
                  >
                    Submit
                  </button>
                </div>
              )}
          </div>
          <main>
            {isShowLoading ? (
              <div className="loading-container-modal-product-note">
                <div className="loader-modal-product-note"></div>
              </div>
            ) : (
              <div className="product-table-container">
                <table className="product-table">
                  <thead>
                    <tr>
                      <th width="50px">No</th>
                      <th>tanggal</th>
                      <th>staf</th>
                      <th>info</th>
                      <th>in</th>
                      <th>out</th>
                      <th>retur</th>
                      <th>stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productNoteData.map((productNote, index) => (
                      <tr key={index}>
                        <td>{(page - 1) * 10 + index + 1}</td>
                        <td>
                          {formatDate(
                            productNote.product_transaction_note_date
                          )}
                        </td>
                        <td>{productNote.staff_name}</td>
                        <td>{productNote.product_transaction_note_message}</td>
                        <td>{productNote.product_restock_number}</td>
                        <td>{productNote.product_sold_number}</td>
                        <td>{productNote.product_refund_number}</td>
                        <td>{productNote.product_stock_history}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </main>
          <footer className="modal-product-note-footer">
            <PaginationFooter
              isPreviousActive={page > 1}
              isNextActive={page < totalPage}
              onNextClick={onNextPageClicked}
              onPrevClick={onPreviousPageClicked}
            />
          </footer>
        </div>
      </div>
    ),
    document.getElementById("modal")
  );
};

export default ModalProductInfo;
